import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import {
  normalizeExperimentDetails,
  type ExperimentDetailsValues,
} from '../../../utils/experimentDetails';

interface EditExperimentDetailsDialogProps {
  open: boolean;
  initialValues: ExperimentDetailsValues;
  onClose: () => void;
  onSave: (values: ExperimentDetailsValues) => Promise<void>;
}

export function EditExperimentDetailsDialog({
  open,
  initialValues,
  onClose,
  onSave,
}: EditExperimentDetailsDialogProps): JSX.Element {
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [nameTouched, setNameTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialValues.name);
    setDescription(initialValues.description);
    setNameTouched(false);
    setSaving(false);
    setError(null);
  }, [initialValues.description, initialValues.name, open]);

  const normalizedValues = useMemo(
    () => normalizeExperimentDetails({ name, description }),
    [description, name],
  );
  const normalizedInitialValues = useMemo(
    () =>
      normalizeExperimentDetails({
        name: initialValues.name,
        description: initialValues.description,
      }),
    [initialValues.description, initialValues.name],
  );
  const nameIsBlank = normalizedValues.name.length === 0;
  const hasChanges =
    normalizedValues.name !== normalizedInitialValues.name ||
    normalizedValues.description !== normalizedInitialValues.description;
  const canSave = !saving && !nameIsBlank && hasChanges;

  function handleClose(): void {
    if (!saving) onClose();
  }

  async function handleSave(): Promise<void> {
    setNameTouched(true);
    if (!canSave) return;

    setSaving(true);
    setError(null);
    try {
      await onSave(normalizedValues);
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : 'Failed to update experiment.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit experiment details</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            autoFocus
            required
            fullWidth
            label="Test name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && nameIsBlank}
            helperText={nameTouched && nameIsBlank ? 'Test name is required.' : undefined}
            disabled={saving}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
